import { InteractionStatus } from "@azure/msal-browser";
import { Box, Grid } from "@mui/material";
import FormSkeleton from "common/skeleton";
import { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import LinksSection from "components/Links";
import FileGrid from "components/ObjectList";
import { getLoggedInUser } from "utils/utils";
import { useNavigate } from "react-router-dom";
import { ISusData } from "components/SpaceList";
import type { ColumnsType } from "antd/es/table";
import { deleteSpace } from "components/grid/gridApi";
import AddOrEditSpace from "components/AddOrEditSpace";
import { NormalizedSpace } from "utils/normalizeSpaces";
import { IrequestBody } from "components/grid";
import { formSkeleton } from "components/grid/constants";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import ErrorModal from "components/ErrorModal";
import { getSpacesByDomainName } from "components/SpaceList/spaceListApi";
import { CopyOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Empty, Table, Tooltip, message, Menu, Dropdown, Tag, Divider } from "antd";
import { safeParseMetadataUsers } from "helpers";
import { getS3grantAccessToken } from "config/token";
import "components/grid/style.scss";

interface IProps {
  spaces: NormalizedSpace[] | undefined;
  domainName: string;
  onRefreshSpaces: () => void;
  isSpaceListFetching: boolean;
  onDeleteAccess: (metaDataUsers: string[]) => void;
}

interface SpaceRow {
  spaceName: string;
  bucketName: string;
  regionName: string;
  projectType: "Data" | "Analytics" | "–";
  profileType: "Store" | "Explore" | "–";
  gxpFlag: "GxP" | "Non-GxP" | "–";
  expiration: string;
  __onClick__?: (space: string, region: string, spacePrefix: string, bucketName: string, spaceDisplayName: string, sus_git_repo: string, sus_project_id: string, sus_project_link: string, sus_project_metadata: string, gxpFlag: string, data_account_id: string, metaDataUsers?: Array<{ user_email: string; access_level: string; }>, fileSystemType?: string) => void;
  metaDataUsers: string;
  sus_git_repo: string;
  sus_project_id: string;
  sus_project_link: string;
  sus_project_metadata: string;
  data_account_id: string;
  file_system_type: string;
}

type MetaDataUser = { user_email: string; access_level: string; };

const isMetaDataUser = (value: unknown): value is MetaDataUser => {
  if (typeof value !== "object" || value === null) return false;

  const candidate = value as Record<string, unknown>;
  return typeof candidate.user_email === "string" && typeof candidate.access_level === "string";
};

const normalizeMetaDataUsers = (value: unknown): MetaDataUser[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(isMetaDataUser);
};

const parseMetaDataUsers = (metaDataUsers?: string): MetaDataUser[] => {
  if (!metaDataUsers) return [];

  try {
    const parsed: unknown = JSON.parse(metaDataUsers);

    if (Array.isArray(parsed)) return normalizeMetaDataUsers(parsed);

    // Handle legacy/double-encoded payloads safely.
    if (typeof parsed === "string") {
      const reparsed: unknown = JSON.parse(parsed);
      return normalizeMetaDataUsers(reparsed);
    }

    return [];
  } catch {
    return [];
  }
};

export default function SpaceObjects(props: IProps) {
  const { spaces, domainName, onRefreshSpaces, isSpaceListFetching } = props;
  const [requestBody, setRequestBody] = useState<IrequestBody>({
    region: "",
    data_domain_name: "",
    bucket_name: "",
    space_name: "",
    data_account_id: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPrefix, setCurrentPrefix] = useState<string>("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedSpaceGxpFlag, setSelectedSpaceGxpFlag] = useState<string>("");
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>("");
  const [selectedDataAccountID, setSelectedDataAccountID] = useState<string>("");
  const [selectedProfileType, setSelectedProfileType] = useState<string>("");
  const [selectedSpaceBucketName, setSelectedSpaceBucketName] = useState<string>("");
  const [selectedSpaceRegion, setSelectedSpaceRegion] = useState<string | null>(null);
  const [selectedSpaceDisplayName, setSelectedSpaceDisplayName] = useState<string>("");
  const [selectedSpaceUserPermissions, setSelectedSpaceUserPermissions] = useState<Array<{ user_email: string; access_level: string; }>>([]);
  const [selectedSpaceFileSystemType, setSelectedSpaceFileSystemType] = useState<string>("");
  const [susData, setSusData] = useState<ISusData>({
    gitRepo: "",
    projectId: "",
    projectLink: "",
    susProjectMetadata: "",
  });
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["all"]);
  const [spaceObjects, setSpaceObjects] = useState<{ data: any } | null>(null);

  const navigate = useNavigate();
  const msalInstance = useMsal();

const authReady =
  msalInstance.inProgress === InteractionStatus.None &&
  msalInstance.accounts.length > 0;
 

  // Show delete modal
  const showDeleteModal = () => setIsDeleteModalVisible(true);

  // Handle domain selection
  const columns: ColumnsType<SpaceRow> = [
    {
      title: "Space Name",
      dataIndex: "spaceName",
      key: "spaceName",
      render: (text, record) => {
        return (
          <span className="space-name-wrapper">
            <Tooltip title={text}>
              <span
                className="space-name-text"
                onClick={() => {
                  record.__onClick__?.(
                    record.spaceName,
                    record.regionName,
                    "",
                    record.bucketName,
                    record.spaceName,
                    record.sus_git_repo,
                    record?.sus_project_id,
                    record?.sus_project_link,
                    record?.sus_project_metadata,
                    record.gxpFlag,
                    record.data_account_id,
                    JSON.parse(record.metaDataUsers),
                    record.file_system_type,
                  );
                }}
              >
                {text}
              </span>
            </Tooltip>
            <Tooltip title="Copy space name">
              <CopyOutlined
                className="space-name-copy-icon"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(text);
                    message.success("Copied space name!");
                  } catch {
                    message.error("Failed to copy space name");
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },

    },
    {
      title: "Region",
      dataIndex: "regionName",
      key: "regionName",
    },
    {
      title: "Project Type",
      dataIndex: "projectType",
      key: "projectType",
      onFilter: (value, record) => record.projectType === value,
    },
    {
      title: "Profile Type",
      dataIndex: "profileType",
      key: "profileType",
      onFilter: (value, record) => record.profileType === value,
    },
    {
      title: "GxP / Non-GxP",
      dataIndex: "gxpFlag",
      key: "gxpFlag",

      render: (_: any, record: SpaceRow) => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {record.gxpFlag.toLocaleLowerCase() === "nongxp" ? (
            <Tag color="green">{record.gxpFlag}</Tag>
          ) : (
            <Tag color="blue">{record.gxpFlag}</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Expires On",
      dataIndex: "expiration",
      key: "expiration",
      // Optionally add filters if needed
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: SpaceRow) => (
        <Dropdown
          overlay={
            <Menu className="action-menu-custom">
              <Menu.Item
                key="copyS3"
                style={{
                  color: "#1677ff",
                  fontWeight: 500,
                  fontSize: 15,
                  paddingLeft: 16,
                }}
                onClick={async (e) => {
                  e.domEvent.stopPropagation();
                  const s3Uri = `s3://${record.bucketName}`;

                  try {
                    await navigator.clipboard.writeText(s3Uri);
                    message.success("Copied S3 URI to clipboard!");
                  } catch {
                    message.error("Failed to copy URI");
                  }
                }}
              >
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                  <CopyOutlined style={{ fontSize: 16 }} />
                  Copy S3 URI
                </span>
              </Menu.Item>
              <Menu.Item
                key="copyS3"
                style={{
                  color: "#1677ff",
                  fontWeight: 500,
                  fontSize: 15,
                  paddingLeft: 16,
                }}
                onClick={async (e) => {
                  e.domEvent.stopPropagation();
                  const spaceURI = `${window.location.origin}${window.location.pathname}`;

                  try {
                    await navigator.clipboard.writeText(spaceURI);
                    message.success("Copied Space URI to clipboard!");
                  } catch {
                    message.error("Failed to copy URI");
                  }
                }}
              >
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                  <CopyOutlined style={{ fontSize: 16 }} />
                  Copy Space URI
                </span>
              </Menu.Item>
              <Menu.Item
                key="editSpace"
                style={{
                  color: "#1677ff",
                  fontWeight: 500,
                  fontSize: 15,
                  paddingLeft: 16,
                }}
              >
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}>
                  <AddOrEditSpace
                    mode="edit"
                    editPayload={{
                      bucketName: record.spaceName,
                      spaceName: record.spaceName,
                      region: record.regionName,
                      domainName: domainName,
                      actualBucketName: record?.bucketName,
                    }}
                    onSuccess={onRefreshSpaces}
                    buttonText="View/Edit Metadata"
                    buttonProps={{
                      type: "link",
                      style: {
                        padding: 0,
                        color: "#1677ff",
                        fontWeight: 500,
                        background: "none",
                        border: "none",
                        boxShadow: "none",
                        height: "auto",
                        textAlign: "left",
                        fontSize: 15,
                      },
                    }}
                    domainName={domainName}
                  />
                </span>
              </Menu.Item>
              <Menu.Item
                key="deleteSpace"
                style={{
                  color: "#ff4d4f",
                  fontWeight: 500,
                  fontSize: 15,
                  paddingLeft: 16,
                }}
                onClick={() => {                  
                  const requestBody: IrequestBody = {
                    "region": record.regionName,
                    "data_domain_name": domainName,
                    "bucket_name": record.bucketName,
                    "space_name": record.spaceName,
                    "data_account_id": record.data_account_id,
                  };
                  setRequestBody(requestBody);
                  setSelectedSpaceGxpFlag(record.gxpFlag);
                  showDeleteModal();
                }}
              >
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                  <DeleteOutlined style={{ fontSize: 16 }} />
                  Delete Space
                </span>
              </Menu.Item>
            </Menu>
          }
          trigger={["hover"]}
          placement="bottomLeft"
          overlayClassName="action-menu-dropdown"
        >
          <span
            style={{
              cursor: "pointer",
              fontSize: 21,
              color: "#1677ff",
              padding: "6px 12px",
              borderRadius: 6,
              display: "inline-block",
              transition: "background 0.2s",
            }}
          >
            <MoreOutlined />
          </span>
        </Dropdown>
      ),
    },
  ];

  // Memoized handleViewObjects to avoid unnecessary re-renders and redundant calls
  const handleViewObjects = async (
    spaceName: string,
    region: string,
    spacePrefix: string,
    bucketName: string,
    spaceDisplayName: string,
    sus_git_repo: string,
    sus_project_id: string,
    sus_project_link: string,
    sus_project_metadata: string,
    gxpFlag: string,
    data_account_id: string,
    profileType: string,
    metaDataUsers?: Array<{ user_email: string; access_level: string; }>,
    fileSystemType?: string,
  ) => {
    // Refresh token before setting state
    try {
      setLoading(true);
      const tokenResult = await getS3grantAccessToken(msalInstance, true);
      if (!tokenResult) {
        setLoading(false);
        setSelectedSpace(null);
        message.error("Failed to refresh token. Please sign in again.");
        return;
      }
    } catch (error) {
      setLoading(false);
      setSelectedSpace(null);
      console.error("Failed to refresh token before viewing space:", error);
      message.error("Failed to refresh token. Please sign in again.");
      return;
    }
        
    setCurrentPrefix(spacePrefix);
    setSelectedSpace(spaceName);
    setSelectedSpaceRegion(region);
    setSelectedSpaceDisplayName(spaceDisplayName);
    setSelectedSpaceBucketName(bucketName);
    setSelectedSpaceGxpFlag(gxpFlag);
    setSelectedDataAccountID(data_account_id);
    setSelectedProfileType(profileType);
    setSelectedSpaceUserPermissions(metaDataUsers || []);
    setSelectedSpaceFileSystemType(fileSystemType || "");
    setSusData({
      gitRepo: sus_git_repo,
      projectId: sus_project_id,
      projectLink: sus_project_link,
      susProjectMetadata: sus_project_metadata,
    });
    setLoading(false);
  };
  // Initial load logic extracted for clarity
useEffect(() => {
  let isCancelled = false;
  if (!authReady) return;
  if (!spaces || spaces.length === 0) return;

  const loadObjects = () => {
    if (isCancelled) return;
    
    const space = spaces[0];
    const parsedUsers = safeParseMetadataUsers(space.metaDataUsers);

    handleViewObjects(
      space.spaceName,
      space.regionName,
      "",
      space.bucketName,
      space.spaceName,
      space.sus_git_repo,
      space?.sus_project_id,
      space?.sus_project_link,
      space?.sus_project_metadata,
      space.gxpFlag,
      space.data_account_id,
      space.profileType,
      parsedUsers,
      space.file_system_type || "",
    );
  };

  loadObjects();
  return () => { isCancelled = true; };
}, [authReady, spaces]);

  // Prepare rows for the table
  const rows = spaces?.map((space: NormalizedSpace) => ({
    key: space.spaceName,
    spaceName: space.spaceName,
    regionName: space.regionName,
    projectType: space.projectType,
    profileType: space.profileType,
    gxpFlag: space.gxpFlag,
    expiration: space.expiration,
    bucketName: space.bucketName,
    data_account_id: space.data_account_id,
    metaDataUsers: space.metaDataUsers || "[]",
    sus_git_repo: space.sus_git_repo,
    sus_project_id: space.sus_project_id,
    sus_project_link: space.sus_project_link,
    file_system_type: space.file_system_type || "",
    sus_project_metadata: space.sus_project_metadata,
    __onClick__: (spaceName: string, region: string, spacePrefix: string, bucketName: string, spaceDisplayName: string, sus_git_repo: string, sus_project_id: string, sus_project_link: string, sus_project_metadata: string, gxpFlag: string, data_account_id: string, metaDataUsers?: Array<{ user_email: string; access_level: string; }>, fileSystemType?: string) => {
      handleViewObjects(
        spaceName,
        region,
        spacePrefix,
        bucketName,
        spaceDisplayName,
        sus_git_repo,
        sus_project_id,
        sus_project_link,
        sus_project_metadata,
        gxpFlag,
        data_account_id,
        space.profileType,
        metaDataUsers,
        fileSystemType,
      );
    },
  }));

  // Handle confirm action in the delete modal
  const handleConfirm = async (hardDelete: boolean, reason: { value: string, label: string }) => {
    setIsDeleteModalVisible(false);
    
    // Check if this is a Non-GxP space
    const isNonGxpSpace = selectedSpaceGxpFlag?.toLowerCase() === "nongxp" || selectedSpaceGxpFlag?.toLowerCase() === "non-gxp";
    const bucketType = isNonGxpSpace ? "nongxp" : "gxp";
    // For Non-GxP spaces, don't include reason and harddelete in the payload
    const payload = isNonGxpSpace 
      ? { ...requestBody, bucket_type: bucketType }
      : { ...requestBody, reason: reason?.value, harddelete: hardDelete, bucket_type: bucketType };
    
    const response = await deleteSpace(msalInstance, payload);
    
    if (response.success) {
      const successMessage = isNonGxpSpace
        ? `Deletion of space ${payload.space_name} has been queued. You will be notified via email once it is completed`
        : `Deletion of space ${payload.space_name} with reason ${reason.label} has been queued. You will be notified via email once it is completed`;
      message.success(successMessage);
    } else {
      // Check if this is a specific violation error that should show in popup
      const errorMessage = response.error || `Error deleting the space ${payload.space_name}.`;
      
      if (errorMessage.includes("Action restricted: Non-GxP Space containing active files cannot be deleted") ||
          errorMessage.includes("Action restricted: GxP Space containing active files or files with delete marker cannot be deleted")) {
        // Show error modal for specific violation errors
        setErrorModalMessage(errorMessage);
        setIsErrorModalVisible(true);
      } else {
        // Show toast for other errors
        const displayMessage = `Error while deleting the Space. ${errorMessage}`;
        message.error(displayMessage);
      }
    }
  };

  // Handle cancel action in the delete modal
  const handleCancel = () => {
    message.info("Deletion cancelled");
    setIsDeleteModalVisible(false);
  };

  const selectedRegionLabel =
    selectedRegions.length === 1
      ? selectedRegions[0]
      : selectedRegions.length === 0 || selectedRegions.includes("all")
        ? "All"
        : selectedRegions.join(",");

  const isSpaceDetailsLoading = loading;
  const hasSpaceSelectionContext = Boolean(selectedSpaceBucketName || selectedSpaceDisplayName);
  const canShowSpaceDetails = Boolean(selectedSpace) && Boolean(spaces?.length) && !isSpaceListFetching;
  const shouldShowPermissionMessage =
    hasSpaceSelectionContext && !isSpaceDetailsLoading && !canShowSpaceDetails && !isSpaceListFetching;

  const renderSpaceDetailsSection = () => {
    if (isSpaceDetailsLoading) {
      return <FormSkeleton layout={formSkeleton} />;
    }

    if (canShowSpaceDetails) {
      return (
        <Box>
          <Divider />
          <FileGrid
            domainName={domainName}
            selectedRegion={selectedRegionLabel}
            spaceName={selectedSpaceBucketName}
            spaceDisplayName={selectedSpaceDisplayName}
            currentPrefix={currentPrefix}
            selecteSpaceRegion={selectedSpaceRegion}
            selectedBucket={selectedSpaceBucketName}
            gxpFlag={selectedSpaceGxpFlag}
            DataAccountId={selectedDataAccountID}
            fileSystemType={selectedSpaceFileSystemType}
            onFolderClick={(selectedSpace, selected_region, prefix, bucketName, spaceDisplayName) => {
              handleViewObjects(
                selectedSpace,
                selected_region,
                prefix,
                bucketName,
                spaceDisplayName,
                susData?.gitRepo,
                susData?.projectId,
                susData?.projectLink,
                susData?.susProjectMetadata,
                selectedSpaceGxpFlag,
                selectedDataAccountID,
                selectedProfileType,
                selectedSpaceUserPermissions,
                selectedSpaceFileSystemType,
              );
            }}
            onRefreshSpaces={onRefreshSpaces}
            setSelectedSpace={setSelectedSpace}
          />

          <Divider />
          <LinksSection
            spaceDisplayName={selectedSpaceDisplayName}
            sageMakerData={susData}
            domainName={domainName}
            region={selectedSpaceRegion || undefined}
            bucketName={selectedSpaceBucketName}
            dataAccountId={selectedDataAccountID}
            profileType={selectedProfileType}
          />
        </Box>
      );
    }

    if (shouldShowPermissionMessage) {
      return (
        <Grid sx={{ marginTop: "20px" }} container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Empty description="You don't have permission to view this Space" />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <div>
      <Box sx={{ width: "100%", mx: "auto" }}>
        {isSpaceListFetching ? (
          <FormSkeleton layout={formSkeleton} />
        ) : spaces?.length === 0 ? (
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={12}>
              <Empty description="No folders found" />
            </Grid>
          </Grid>
        ) : (

          <div>
            <div style={{ marginBottom: "15px", width: "100%", display: "flex", justifyContent: "start", alignItems: "center" }}>
              {domainName && (
                <Tooltip title="Back" >
                  <button
                    className="back-button"
                    onClick={() => navigate(`/explorer/${domainName}`)}
                  >
                    ← Back
                  </button>
                </Tooltip>
              )}

            </div>
            <Table
              dataSource={rows as SpaceRow[]}
              scroll={{ x: 800 }} // Enables horizontal scroll on small screens
              columns={columns}
              pagination={false}
            />
          </div>
        )}
        <Box sx={{ width: "100%", mx: "auto" }}>
          {renderSpaceDetailsSection()}
        </Box>
      </Box>
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        spaceType={selectedSpaceGxpFlag}
        gitRepoLink={susData?.gitRepo}
      />      
      <ErrorModal
        visible={isErrorModalVisible}
        title="Space Deletion Restricted"
        message={errorModalMessage}
        type="restriction"
        onClose={() => setIsErrorModalVisible(false)}
      />
    </div>
  );
}
